import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { useStoreAppColor } from '@state/useStoreAppColor';

/**
 * Shown when the server refuses an order because a product sold out, or left the menu,
 * after this page loaded.
 *
 * The customer's menu is a snapshot from when they opened the app; the operator can
 * change it underneath them. Until now that ended in "Something went wrong" with no way
 * to tell which product was the problem, so the only recovery was emptying the cart.
 *
 * Nothing was ordered when this appears — the server writes nothing before refusing — so
 * the cart is intact and the customer stands on it: removing the named items here lets
 * them order the rest, and closing leaves the cart untouched to edit by hand. Either way
 * they can confirm again, and be refused again, as the menu keeps changing underneath.
 *
 * `entries` are cart lines, not raw PLUs (see resolveUnavailable). A package is named as
 * the package, and every refused component is listed under it with its own reason,
 * because "the package is rejected" alone does not tell the cashier what to say.
 */
export default function ItemsUnavailableDialog({ open, entries = [], onRemove, onClose }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { appColor } = useStoreAppColor((state) => ({ appColor: state.appColor }));

  // Not theme.palette.text.secondary: that is the store's *secondary font colour*, meant
  // for text sitting on the primary theme colour — white for most stores, as on the
  // button below. On this white dialog it made the body, every reason line ("Unavailable
  // because … is sold out") and the "Edit my cart" button invisible, so QA saw a package
  // named with no explanation. text.grey is the app's fixed readable grey on white.
  const mutedColor = theme.palette.text.grey;

  const isSoldOut = (entry) =>
    entry.reason === 'sold_out' || entry.causes?.some((c) => c.reason === 'sold_out');

  // Sold-out first: that is the one the customer is most likely to ask about.
  const ordered = [...entries].sort((a, b) => Number(isSoldOut(b)) - Number(isSoldOut(a)));

  const title =
    entries.length === 1 && isSoldOut(entries[0]) && !entries[0].causes?.length
      ? t('itemSoldOutTitle', 'This item just sold out')
      : t('itemsUnavailableTitle', 'Some items are no longer available');

  const reasonText = (reason) =>
    reason === 'sold_out'
      ? t('itemReasonSoldOut', 'Sold out')
      : t('itemReasonUnavailable', 'No longer on the menu');

  /** Why this line cannot be ordered, naming every refused component of a package. */
  const describe = (entry) => {
    const causes = entry.causes ?? [];
    if (causes.length === 0) {
      return reasonText(entry.reason);
    }

    // One culprit and nothing else to say: a sentence reads better than a bullet.
    if (causes.length === 1 && !causes[0].on && !entry.reason) {
      return causes[0].reason === 'sold_out'
        ? t('itemUnavailableBecauseSoldOut', {
            defaultValue: 'Unavailable because {{product}} is sold out',
            product: causes[0].plu_name,
          })
        : t('itemUnavailableBecauseRemoved', {
            defaultValue: 'Unavailable because {{product}} is no longer on the menu',
            product: causes[0].plu_name,
          });
    }

    return (
      <>
        {entry.reason
          ? reasonText(entry.reason)
          : t('itemUnavailableBecause', 'Unavailable because:')}
        <Box
          component="ul"
          sx={{ margin: 0, paddingLeft: theme.spacing(2), listStylePosition: 'outside' }}>
          {causes.map((cause, index) => (
            <li key={`${cause.plu_number}-${cause.on ?? ''}-${index}`}>
              {cause.on
                ? t('itemCauseOnComponent', {
                    defaultValue: '{{product}} in {{component}} — {{reason}}',
                    product: cause.plu_name,
                    component: cause.on,
                    reason: reasonText(cause.reason),
                  })
                : `${cause.plu_name} — ${reasonText(cause.reason)}`}
            </li>
          ))}
        </Box>
      </>
    );
  };

  return (
    // The checkout layers are: 1300 the CheckoutPage overlay, 1400 ConfirmDialog and
    // PaymentResult, 1500 LoadingModal. The confirm overlay is closed by the time this
    // opens, but a dialog left at MUI's default (1300) ties with the CheckoutPage overlay
    // it has to be read over, so it stays above the lot.
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      sx={{ zIndex: 1500 }}>
      <DialogTitle sx={{ paddingBottom: 0 }}>{title}</DialogTitle>

      <DialogContent sx={{ paddingTop: theme.spacing(1), paddingBottom: 0 }}>
        <Typography variant="body2" sx={{ color: mutedColor }}>
          {t(
            'itemsUnavailableBody',
            'Your order was not placed and your cart was kept. Please remove these items to order the rest.',
          )}
        </Typography>

        {/* A long list scrolls inside the dialog instead of pushing the buttons off
            screen — a whole package's components can be refused at once. */}
        <List
          dense
          disablePadding
          sx={{
            marginTop: theme.spacing(1),
            maxHeight: '45vh',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
          }}>
          {ordered.map((entry) => (
            <ListItem key={entry.key} disableGutters sx={{ paddingTop: 0, paddingBottom: theme.spacing(0.5), alignItems: 'flex-start' }}>
              <ListItemText
                primary={entry.name}
                secondary={describe(entry)}
                sx={{ margin: 0 }}
                secondaryTypographyProps={{
                  component: 'div',
                  variant: 'body2',
                  sx: { color: mutedColor },
                }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions sx={{ padding: theme.spacing(1.5), justifyContent: 'center' }}>
        <Button onClick={onClose} sx={{ color: mutedColor }}>
          {t('editCartMyself', 'Edit my cart')}
        </Button>
        <Button
          onClick={onRemove}
          variant="contained"
          sx={{
            color: appColor.secondaryFontColor,
            backgroundColor: appColor.primaryThemeColor,
            '&:hover': { backgroundColor: appColor.primaryThemeColor },
          }}>
          {entries.length === 1
            ? t('removeItemFromCart', 'Remove it from my cart')
            : t('removeItemsFromCart', 'Remove them from my cart')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
